"use client";

import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { getMyClientFeatures } from "@/lib/api";

const ALL_FEATURES = ["upload", "banking", "credits", "tickets", "appointments", "invoices", "approval"] as const;
export type FeatureName = typeof ALL_FEATURES[number];

interface FeaturesCtx {
  features: Record<FeatureName, boolean>;
  loading: boolean;
  has: (name: FeatureName) => boolean;
}

const Ctx = createContext<FeaturesCtx>({
  features: { upload: true, banking: false, credits: false, tickets: false, appointments: false, invoices: false, approval: false },
  loading: true,
  has: () => false,
});

export function ClientFeaturesProvider({ children }: { children: ReactNode }) {
  const [features, setFeatures] = useState<Record<FeatureName, boolean>>({
    upload: true, banking: false, credits: false, tickets: false, appointments: false, invoices: false, approval: false,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getMyClientFeatures()
      .then((data: any) => {
        if (cancelled || !data?.features) return;
        const merged: any = { upload: true };
        for (const k of ALL_FEATURES) merged[k] = !!data.features[k];
        merged.upload = true;
        setFeatures(merged);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <Ctx.Provider value={{ features, loading, has: (n) => !!features[n] }}>
      {children}
    </Ctx.Provider>
  );
}

export function useClientFeatures() {
  return useContext(Ctx);
}
